import { useMemo } from 'react';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import CriteriaChecks from '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks';
import RecentSectionUpdates from '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates';
import { getStatementChecks } from '../../../Introduction/utils';
import {
  HEALTH_STATEMENTS_DEFAULT,
  HEALTH_STATEMENTS_FINANCIAL,
  HEALTH_STATEMENTS_LOSS_MAKING,
} from '../../constants';
import loadable from '@loadable/component';

const KeyInformationBank = loadable(
  () => import('./components/KeyInformationBank')
);

const KeyInformationNonBank = loadable(
  () => import('./components/KeyInformationNonBank')
);

export const Header = () => {
  const canonicalURL = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL,
  });
  const {
    IsProfitable: isProfitable,
    IsProfitableLongTerm: isProfitableLongTerm,
    IsBank: isBank,
  } = statements || {};

  const isLossMaking =
    isProfitable?.value === false && isProfitableLongTerm?.value === false;
  const isFinancialCompany = isBank?.value === true;

  const healthStatements = useMemo(() => {
    if (isFinancialCompany) {
      return HEALTH_STATEMENTS_FINANCIAL;
    }
    if (isLossMaking) {
      return HEALTH_STATEMENTS_LOSS_MAKING;
    }
    return HEALTH_STATEMENTS_DEFAULT;
  }, [isLossMaking, isFinancialCompany]);

  const statementChecks = getStatementChecks(healthStatements, statements);

  return (
    <Introduction
      scoreName="Financial Health"
      criteriaChecks={
        <CriteriaChecks scoreName="Financial Health" checks={statementChecks} />
      }
      keyInformation={
        isFinancialCompany ? (
          <KeyInformationBank canonicalURL={canonicalURL} />
        ) : (
          <KeyInformationNonBank canonicalURL={canonicalURL} />
        )
      }
      recentSectionUpdates={
        <RecentSectionUpdates
          lookup={canonicalURL}
          title="Recent financial health updates"
          filterByEventCategory="financial-health"
        />
      }
    />
  );
};
